import { EcoaParagraph } from "@components/common/EcoaParagraph";
import { VisitQuestionnaireInner } from "@components/ecoa/questionnaire/VisitQuestionnaireInner";
import { VisitQuestionnaireMultipleItemInner } from "@components/ecoa/questionnaire/VisitQuestionnaireMultipleItemInner";
import { VisitQuestionnaireWrapper } from "@components/ecoa/questionnaire/VisitQuestionnaireWrapper";
import { sessionSlice } from "@maven-rest/states";
import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import uniqWith from "lodash/uniqWith";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { useRequest } from "@coralblack/flax";
import { EcoaStudyCapture, useCaptureContext } from "@layouts/ecoa/CaptureContext";
import { useLangContext } from "@layouts/home/LangContext";
import {
  CoStringSpec,
  CrfCheckItemSpec,
  CrfItemCommonDescription,
  CrfSectionItemRefAvailabilitySpec,
  CrfSingleSelectItemSpec,
  EdcGroupId,
  InitialCrfPageSequence,
  InitialItemSequence,
  StudySpec,
} from "maven-lib/dist/consts/edc";
import { hasOwnProperty, isAvailableFor, isDisableFor } from "maven-lib/dist/consts/edc/lib";
import { GetEcoaCapturesResponse } from "maven-lib/dist/dto/ecoaCapture";
import { EcoaVisitQuestionnaireData } from "maven-lib/dist/dto/ecoaVisit";
import { EcoaCrf } from "maven-lib/dist/dto/entity/ecoaCrf";
import { EcoaQuestionnaire } from "maven-lib/dist/dto/entity/ecoaVisit";
import { ecoaCaptureApi } from "maven-lib/dist/states/apis/ecoaCapture";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { nl2br } from "@maven-surface/components/helper/nl2br";
import { useIntParam, useRoute } from "@maven-surface/hooks/useRoute";
import { CDMS_STUDY_STATUS_CODE } from "@maven-msg/code/cdms/studyStatus";
import { flatten } from "lodash";

interface VisitQuestionnaireProps {
  studySpec: StudySpec;
  crfs: Array<EcoaCrf>;
  folderId: string;
  visitId: string;
  visitSeq: number;
  crfPageId: string;
  crfPageSeq: number;
  questionnairesData: EcoaVisitQuestionnaireData;
}

export type VisitQuestionnaireAnswer = {
  itemId: string | null;
  dbVal: string | number | null;
  calcVal?: string | number | null;
};

interface StyledVisitQuestionnaireWrapperProps {
  isBottomDescription: boolean;
}

const StyledVisitQuestionnaireWrapper = styled.div<StyledVisitQuestionnaireWrapperProps>`
  ${({ isBottomDescription }) =>
    isBottomDescription &&
    `
    overflow: auto;

    .app-hybrid-content-wrapper {
      padding-bottom: 1rem;
    }

    p:last-child {
      margin-bottom: 72px;
    }
  `}
`;

export function VisitQuestionnaire(props: VisitQuestionnaireProps) {
  const { studySpec, crfs, folderId, visitId, visitSeq, crfPageId, crfPageSeq, questionnairesData } = props;
  const { questionnaires } = questionnairesData;

  const params = useParams<{ questionnaireSeq: string }>();
  const questionnaireSeq = useIntParam(params.questionnaireSeq);
  const { pushPath } = useRoute();
  const { fontSize } = useLangContext();
  const { setCaptures } = useCaptureContext();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [captureResponse, setCaptureResponse] = useState<GetEcoaCapturesResponse | null>(null);
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState<Array<VisitQuestionnaireAnswer>>([]);

  const capturingOn = { groupId: EdcGroupId, folderId, visitId, visitSeq, crfPageId, crfPageSeq };
  const capturedItems = useMemo(
    () =>
      captureResponse
        ? uniqWith(
            [...captureResponse.captured.temp, ...captureResponse.captured.items],
            (a, b) =>
              a.groupId === b.groupId &&
              a.folderId === b.folderId &&
              a.visitId === b.visitId &&
              a.visitSeq === b.visitSeq &&
              a.crfPageId === b.crfPageId &&
              a.crfPageSeq === b.crfPageSeq &&
              a.itemId === b.itemId &&
              a.itemSeq === b.itemSeq
          )
        : [],
    [captureResponse]
  );

  const getPendingItems = (questionnaire: EcoaQuestionnaire, answers: Array<VisitQuestionnaireAnswer>) => {
    const answerQuestionnaire =
      flatten(filteredQuestionnaires).find((q) => !!q && answers.find((a) => a.itemId === q.item.id)) ?? questionnaire;

    if (!answerQuestionnaire) return [];

    return answers
      .map(({ itemId, dbVal }) => ({
        itemId: itemId || "",
        dbVal,
        sectionId: answerQuestionnaire.item.sectionId,
        itemSeq: answerQuestionnaire.item.original.itemSeq || InitialItemSequence,
      }))
      .map(({ sectionId, itemId, itemSeq, dbVal }) => ({
        id: BigInt(0),
        groupId: EdcGroupId,
        folderId,
        visitId,
        visitSeq,
        crfPageId,
        crfPageSeq: crfPageSeq ?? InitialCrfPageSequence,
        sectionId,
        itemId,
        itemSeq,
        dbVal,
        calcVal: null,
        freshman: false,
        deleted: false,
      }));
  };

  const filteredQuestionnaires = useMemo(
    () =>
      ((questionnaires) => {
        const itemFilter = (q: EcoaQuestionnaire) => {
          // NOTE: 지원되지 않는 Item이거나 hideOnEcoa인 경우 Rendering 되지 않도록 제외.
          return (
            ["CHECK_GROUP", "SINGLE_SELECT", "TEXT", "CHECK", "DATE", "TIME", "CONSTANT", "AUTO_TEXT", "SLIDER", "DESCRIPTION"].includes(
              q?.item?.type
            ) && !q.item.original.ecoa?.hideOnEcoa
          );
        };

        return uniqWith(
          [
            ...questionnaires
              .map((q) => {
                if (Array.isArray(q)) {
                  return uniqWith(q.filter(itemFilter), (a, b) => {
                    if (a.item.type === "DESCRIPTION" && b.item.type === "DESCRIPTION") {
                      return false;
                    } else {
                      return a.item.groupUuid === b.item.groupUuid;
                    }
                  });
                } else {
                  return itemFilter(q) ? q : null;
                }
              })
              .filter((el) => (Array.isArray(el) ? el.length : !!el)),
          ],
          (a, b) => {
            if (!Array.isArray(a) && !Array.isArray(b)) {
              if (a!.item.type === "DESCRIPTION" && b!.item.type === "DESCRIPTION") {
                return false;
              } else {
                return a!.item.groupUuid === b!.item.groupUuid;
              }
            }
            return false;
          }
        );
      })(questionnaires),
    [questionnaires]
  );

  if (!filteredQuestionnaires.length) {
    pushPath("/pat/visits");
  }

  const matchCapturedItem = (questionnaire: EcoaQuestionnaire | null) => {
    if (!questionnaire) return false;

    return !!captureResponse?.captured.items.find(
      (item) =>
        item.folderId === folderId &&
        item.visitId === visitId &&
        item.visitSeq === visitSeq &&
        item.crfPageId === crfPageId &&
        item.crfPageSeq === crfPageSeq &&
        item.itemId === questionnaire.item.id
    );
  };

  const checkAvailability = (questionnaire: EcoaQuestionnaire, answers: Array<VisitQuestionnaireAnswer>) => {
    const pendingItems = getPendingItems(questionnaire, answers);

    return (
      !questionnaire?.item?.availability ||
      isAvailableFor(
        studySpec,
        capturedItems,
        pendingItems,
        questionnaire.item.availability.map((ava) =>
          hasOwnProperty(ava, "operator") ? ava : { ...ava, seq: InitialItemSequence, ref: "ITEM", id: (ava as any).id.itemId }
        ),
        capturingOn
      )
    );
  };

  const checkDisability = (questionnaire: EcoaQuestionnaire, answers: Array<VisitQuestionnaireAnswer>) => {
    const pendingItems = getPendingItems(questionnaire, answers);

    return (
      !!questionnaire?.item?.disability &&
      isDisableFor(
        studySpec,
        capturedItems,
        pendingItems,
        questionnaire.item.disability.map((ava) =>
          hasOwnProperty(ava, "expr")
            ? ava
            : hasOwnProperty(ava, "operator")
            ? { operator: "AND", expr: [ava] }
            : ({ ...ava, seq: InitialItemSequence, ref: "ITEM", id: (ava as any).id.itemId } as CrfSectionItemRefAvailabilitySpec)
        ),
        capturingOn
      )
    );
  };

  const isAvailableItem = (questionnaire: EcoaQuestionnaire | null, answers: Array<VisitQuestionnaireAnswer>): boolean => {
    if (!questionnaire) return false;
    if (!captureResponse) return true;

    const isAvailable = checkAvailability(questionnaire, answers);
    const isDisable = checkDisability(questionnaire, answers);

    return isAvailable && !isDisable;
  };

  const isSkipItem = (questionnaire: EcoaQuestionnaire | null, answers: Array<VisitQuestionnaireAnswer>) => {
    if (!questionnaire) return false;
    if (!captureResponse) return true;

    const isMatched = matchCapturedItem(questionnaire);
    const isAvailable = checkAvailability(questionnaire, answers);
    const isDisable = (() => {
      if (questionnaire.item.original.ecoa?.noSkipWhenDisabled) return false;

      return checkDisability(questionnaire, answers);
    })();

    return !(isMatched || isAvailable) || isDisable;
  };

  const getItemsFromCapturedData = (
    captureData: GetEcoaCapturesResponse,
    questionnaire: EcoaQuestionnaire
  ): Array<VisitQuestionnaireAnswer> => {
    if (questionnaire.item.original.type === "CHECK") {
      const checkItem: CrfCheckItemSpec = questionnaire.item.original;
      const tempCaptured = captureData.captured.temp
        .filter(
          ({ itemId, itemSeq }) =>
            checkItem.itemCode.codes.find(({ id, typeText }) => id === itemId || typeText?.id === itemId) &&
            itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
        )
        .map(({ itemId, dbVal, calcVal }) => ({ itemId, dbVal, calcVal }));

      const prevCaptured = captureData.captured.items
        .filter(
          ({ itemId, itemSeq }) =>
            checkItem.itemCode.codes.find(({ id, typeText }) => id === itemId || typeText?.id === itemId) &&
            itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
        )
        .map(({ itemId, dbVal, calcVal }) => ({ itemId, dbVal, calcVal }));

      const merged: Array<VisitQuestionnaireAnswer> = checkItem.id ? [{ itemId: checkItem.id, dbVal: null }] : [];

      merged.push(
        ...checkItem.itemCode.codes.map(({ id }) => {
          const dbVal =
            tempCaptured.find(({ itemId }) => itemId === id)?.dbVal ?? prevCaptured.find(({ itemId }) => itemId === id)?.dbVal ?? null;
          const calcVal =
            tempCaptured.find(({ itemId }) => itemId === id)?.calcVal ?? prevCaptured.find(({ itemId }) => itemId === id)?.calcVal ?? null;

          return { itemId: id, dbVal, calcVal };
        })
      );

      questionnaire.item.original.itemCode.codes.forEach((code, index) => {
        if (code?.typeText?.id) {
          const codeTypeTextId = checkItem.itemCode.codes[index].typeText?.id;
          const dbVal =
            captureData.captured.temp.find(
              ({ itemId, itemSeq }) => itemId === codeTypeTextId && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
            )?.dbVal ??
            captureData.captured.items.find(
              ({ itemId, itemSeq }) => itemId === codeTypeTextId && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
            )?.dbVal ??
            null;
          const calcVal =
            captureData.captured.temp.find(
              ({ itemId, itemSeq }) => itemId === codeTypeTextId && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
            )?.calcVal ??
            captureData.captured.items.find(
              ({ itemId, itemSeq }) => itemId === codeTypeTextId && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
            )?.calcVal ??
            null;

          merged.push({ itemId: code.typeText.id, dbVal, calcVal });
        }
      });

      return merged;
    } else if (questionnaire.item.original.type === "SINGLE_SELECT") {
      const selectItem: CrfSingleSelectItemSpec = questionnaire.item.original;
      const responseItem = captureData.captured.temp.find(
        ({ itemId, itemSeq }) =>
          itemId === questionnaire.item.id && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
      ) ||
        captureData.captured.items.find(
          ({ itemId, itemSeq }) =>
            itemId === questionnaire.item.id && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
        ) || { itemId: questionnaire.item.id, dbVal: null, calcVal: null };

      const merged: Array<VisitQuestionnaireAnswer> = [{ itemId: selectItem.id, dbVal: responseItem.dbVal, calcVal: responseItem.calcVal }];

      questionnaire.item.original.itemCode.codes.forEach((code, index) => {
        if (code.typeText?.id) {
          const codeTypeTextId = selectItem.itemCode.codes[index].typeText?.id;
          const dbVal =
            captureData.captured.temp.find(
              ({ itemId, itemSeq }) => itemId === codeTypeTextId && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
            )?.dbVal ??
            captureData.captured.items.find(
              ({ itemId, itemSeq }) => itemId === codeTypeTextId && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
            )?.dbVal ??
            null;
          const calcVal =
            captureData.captured.temp.find(
              ({ itemId, itemSeq }) => itemId === codeTypeTextId && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
            )?.calcVal ??
            captureData.captured.items.find(
              ({ itemId, itemSeq }) => itemId === codeTypeTextId && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
            )?.calcVal ??
            null;

          merged.push({ itemId: code.typeText.id, dbVal, calcVal });
        }
      });

      if (questionnaire.item.original.children?.items.length) {
        questionnaire.item.original.children?.items.forEach((childrenItem) => {
          if (childrenItem.type === "TIME") {
            const timeItem = captureData.captured.temp.find(
              ({ itemId, itemSeq }) => itemId === childrenItem.id && (itemSeq === childrenItem.itemSeq || InitialItemSequence)
            ) ||
              captureData.captured.items.find(
                ({ itemId, itemSeq }) => itemId === childrenItem.id && (itemSeq === childrenItem.itemSeq || InitialItemSequence)
              ) || { itemId: childrenItem.id, dbVal: null, calcVal: null };

            merged.push({ itemId: childrenItem.id, dbVal: timeItem.dbVal, calcVal: timeItem.calcVal });
          } else if (childrenItem.type === "CHECK") {
            childrenItem.itemCode.codes.forEach((code) => {
              const checkItem = captureData.captured.temp.find(
                ({ itemId, itemSeq }) => itemId === code.id && itemSeq === (childrenItem.itemSeq || InitialItemSequence)
              ) ||
                captureData.captured.items.find(
                  ({ itemId, itemSeq }) => itemId === code.id && itemSeq === (childrenItem.itemSeq || InitialItemSequence)
                ) || {
                  itemId: code.id,
                  dbVal: null,
                  calcVal: null,
                };

              merged.push({ itemId: code.id, dbVal: checkItem.dbVal, calcVal: checkItem.calcVal });
            });
          }
        });
      }

      return merged;
    } else if (questionnaire.item.original.type === "DATE") {
      const dateItem = questionnaire.item.original;
      const dateValues: Array<VisitQuestionnaireAnswer> = [];

      const responseItem = captureData.captured.temp.find(
        ({ itemId, itemSeq }) =>
          itemId === questionnaire.item.id && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
      ) ||
        captureData.captured.items.find(
          ({ itemId, itemSeq }) =>
            itemId === questionnaire.item.id && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
        ) || { itemId: questionnaire.item.id, dbVal: null, calcVal: null };

      dateValues.push({ itemId: dateItem.id, dbVal: responseItem.dbVal, calcVal: responseItem.calcVal });

      if (questionnaire.item.original.children?.items.length) {
        questionnaire.item.original.children?.items.forEach((childrenItem) => {
          if (childrenItem.type === "TIME") {
            const timeItem = captureData.captured.temp.find(
              ({ itemId, itemSeq }) => itemId === childrenItem.id && (itemSeq === childrenItem.itemSeq || InitialItemSequence)
            ) ||
              captureData.captured.items.find(
                ({ itemId, itemSeq }) => itemId === childrenItem.id && (itemSeq === childrenItem.itemSeq || InitialItemSequence)
              ) || { itemId: childrenItem.id, dbVal: null, calcVal: null };

            dateValues.push({ itemId: childrenItem.id, dbVal: timeItem.dbVal, calcVal: timeItem.calcVal });
          }
        });
      }

      return dateValues;
    } else {
      const values: Array<VisitQuestionnaireAnswer> = [];

      const responseItem = captureData.captured.temp.find(
        ({ itemId, itemSeq }) =>
          itemId === questionnaire.item.id && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
      ) ||
        captureData.captured.items.find(
          ({ itemId, itemSeq }) =>
            itemId === questionnaire.item.id && itemSeq === (questionnaire.item.original.itemSeq || InitialItemSequence)
        ) || { itemId: questionnaire.item.id, dbVal: null, calcVal: null };

      values.push({ itemId: questionnaire.item.id, dbVal: responseItem.dbVal, calcVal: responseItem.calcVal });

      // children으로 CHECK 아이템 지원
      // Note: 20231013 CHECK 아이템이 children인 경우, typeText를 지원하지 않음 (향후 필요시 코드 추가 구현)
      if (questionnaire.item.original.children?.items.length) {
        questionnaire.item.original.children?.items.forEach((childItem) => {
          if (childItem.type === "CHECK") {
            const checkItem: CrfCheckItemSpec = childItem;
            const tempCaptured = captureData.captured.temp
              .filter(
                ({ itemId, itemSeq }) =>
                  checkItem.itemCode.codes.find(({ id, typeText }) => id === itemId || typeText?.id === itemId) &&
                  itemSeq === (checkItem.itemSeq || InitialItemSequence)
              )
              .map(({ itemId, dbVal, calcVal }) => ({ itemId, dbVal, calcVal }));

            const prevCaptured = captureData.captured.items
              .filter(
                ({ itemId, itemSeq }) =>
                  checkItem.itemCode.codes.find(({ id, typeText }) => id === itemId || typeText?.id === itemId) &&
                  itemSeq === (checkItem.itemSeq || InitialItemSequence)
              )
              .map(({ itemId, dbVal, calcVal }) => ({ itemId, dbVal, calcVal }));

            const merged = checkItem.itemCode.codes.map(({ id }) => {
              const dbVal =
                tempCaptured.find(({ itemId }) => itemId === id)?.dbVal || prevCaptured.find(({ itemId }) => itemId === id)?.dbVal || null;
              const calcVal =
                tempCaptured.find(({ itemId }) => itemId === id)?.calcVal ||
                prevCaptured.find(({ itemId }) => itemId === id)?.calcVal ||
                null;

              return { itemId: id, dbVal, calcVal };
            });

            values.push(...merged);
          }
        });
      }

      return values;
    }
  };

  const availableQuestionnaires = useMemo(() => {
    return filteredQuestionnaires.filter((filteredQuestionnaire) => {
      if (questionnaireAnswers.length === 0) return true;
      if (Array.isArray(filteredQuestionnaire)) {
        return !filteredQuestionnaire.every((q) => isSkipItem(q, questionnaireAnswers));
      } else {
        if (!filteredQuestionnaire) return false;

        return !isSkipItem(filteredQuestionnaire, questionnaireAnswers);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredQuestionnaires, questionnaireSeq, questionnaireAnswers, crfPageSeq]);

  // TODO
  // account 정보 입력 item이 있을 경우,
  // santa로 api 호출하여 금융기관 정보를 내려 받아 이용하도록 수정 작업 예정.
  // 현재 웰트는 crf item codes에 직접 넣어두고 사용하도록 구현되어 있음.
  const questionnaireItem = useMemo(() => availableQuestionnaires[questionnaireSeq - 1], [availableQuestionnaires, questionnaireSeq]);

  const { request: fetchEcoaCaptures } = useRequest(
    {
      ...ecoaCaptureApi.getEcoaCaptures(folderId, visitId, visitSeq, crfPageId, crfPageSeq),
      data: {
        folderId,
        visitId,
        visitSeq,
        crfPageId,
        crfPageSeq,
      },
    },
    {
      success: (responseData) => {
        setCaptureResponse(responseData);
      },
      error: (error) => {
        if (error?.code === CDMS_STUDY_STATUS_CODE.STUDY_CLOSED.code) {
          dispatch(sessionSlice.actions.signOut());

          return error?.message || "An error has occurred.";
        }
      },
    }
  );

  useEffect(() => {
    if (captureResponse) {
      let tempSeqAnswer: VisitQuestionnaireAnswer[] = [];
      let isAvailableQuestionnaireSeq = false;

      for (let i = questionnaireSeq - 1; i < availableQuestionnaires.length; i++) {
        const currentQuestionaires = availableQuestionnaires[i];

        if (!currentQuestionaires) continue;

        if (Array.isArray(currentQuestionaires)) {
          const mergedItems: Array<Array<VisitQuestionnaireAnswer>> = [];

          currentQuestionaires.forEach((questionnaire) => {
            mergedItems.push(getItemsFromCapturedData(captureResponse, questionnaire));
          });

          tempSeqAnswer = flatten(mergedItems);
          isAvailableQuestionnaireSeq = !currentQuestionaires.every((questionnaire) => isSkipItem(questionnaire, flatten(mergedItems)));
        } else {
          tempSeqAnswer = getItemsFromCapturedData(captureResponse, currentQuestionaires);
          isAvailableQuestionnaireSeq = !isSkipItem(currentQuestionaires, tempSeqAnswer);
        }

        if (isAvailableQuestionnaireSeq) break;
      }

      setIsLoading(false);
      setQuestionnaireAnswers(tempSeqAnswer);

      const mergedCaptured =
        uniqWith(
          [...captureResponse.captured.temp, ...captureResponse.captured.items],
          (a, b) =>
            a.groupId === b.groupId &&
            a.folderId === b.folderId &&
            a.visitId === b.visitId &&
            a.visitSeq === b.visitSeq &&
            a.crfPageId === b.crfPageId &&
            a.crfPageSeq === b.crfPageSeq &&
            a.itemId === b.itemId &&
            a.itemSeq === b.itemSeq
        ) || [];
      setCaptures(mergedCaptured);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captureResponse, questionnaireItem]);

  useEffect(() => {
    fetchEcoaCaptures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireSeq]);

  const getSectionId = (answer: VisitQuestionnaireAnswer): string => {
    let sectionId = "";

    for (let i = questionnaireSeq - 1; i < availableQuestionnaires.length; i++) {
      const currentQuestionaires = availableQuestionnaires[i];

      if (!currentQuestionaires) continue;

      if (Array.isArray(currentQuestionaires)) {
        const questionnaire = currentQuestionaires.find((questionnaire) => {
          return questionnaire.item.id === answer.itemId;
        });

        if (questionnaire) {
          sectionId = questionnaire.item.sectionId;

          break;
        }
      } else {
        if (currentQuestionaires.item.id === answer.itemId) {
          sectionId = currentQuestionaires.item.sectionId;

          break;
        }
      }
    }

    return sectionId;
  };

  useEffect(() => {
    const tempCaptures = questionnaireAnswers.map<EcoaStudyCapture>((answer) => ({
      id: null,
      groupId: EdcGroupId,
      folderId: folderId,
      visitId: visitId,
      visitSeq: visitSeq,
      crfPageId: crfPageId,
      crfPageSeq: crfPageSeq,
      sectionId: getSectionId(answer),
      itemId: answer.itemId,
      itemSeq: InitialItemSequence,
      dbVal: answer.dbVal,
      calcVal: answer.calcVal,
      freshman: null,
      deleted: null,
    }));

    setCaptures(tempCaptures);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireAnswers]);

  if (isLoading) {
    return <div className="fx-guard-loading"></div>;
  }

  // NOTE: 현재 질문 문항이 `Array` 라면 한 화면에 여러 문항을 출력해야 하는 경우
  if (Array.isArray(questionnaireItem)) {
    const headQuestionnaireTitle = questionnaires?.length ? (questionnaires as EcoaQuestionnaire[])[0]?.title || "" : "";

    return (
      <VisitQuestionnaireWrapper title={headQuestionnaireTitle}>
        <VisitQuestionnaireMultipleItemInner
          studySpec={studySpec}
          crfs={crfs}
          folderId={folderId}
          visitId={visitId}
          visitSeq={visitSeq}
          crfPageId={crfPageId}
          crfPageSeq={crfPageSeq}
          questionnaireSeq={questionnaireSeq}
          questionnaires={questionnaireItem}
          questionnairesData={{
            ...questionnairesData,
            questionnaires: availableQuestionnaires as Array<EcoaQuestionnaire | Array<EcoaQuestionnaire>>,
          }}
          captureResponse={captureResponse}
          questionnaireAnswersState={[questionnaireAnswers, setQuestionnaireAnswers]}
          isAvailableItem={isAvailableItem}
          getItemsFromCapturedData={getItemsFromCapturedData}
        />
      </VisitQuestionnaireWrapper>
    );
  }
  // NOTE: 한 화면에 한 문항만을 출력하는 경우
  else {
    const descriptionText = (() => {
      const desc = questionnaireItem?.item?.original?.description;

      if (!desc) return "";
      else if (typeof desc === "object") return (desc as CoStringSpec)?.text;
      return desc;
    })();
    const descriptionPosition = (questionnaireItem?.item?.original?.description as CrfItemCommonDescription)?.ecoa?.position || "BOTTOM";
    const descriptionColor = ((color: any) =>
      color === "RED" ? "DANGER" : color === "GRAY" ? "GRAY" : color === "BLUE" ? "PRIMARY" : undefined)(
      (questionnaireItem?.item?.original?.description as CoStringSpec)?.color
    );
    const descriptionSize = fontSize === "LARGE" ? "5X-LARGE" : fontSize === "MEDIUM" ? "3X-LARGE" : "LARGE";
    const isVisibleDescription = questionnaireItem?.item.original.type !== "DESCRIPTION" && !!descriptionText;
    const isBottomDescription = isVisibleDescription && descriptionPosition === "BOTTOM";

    const question = (() => {
      if (
        (questionnaireItem?.item.original.type === "SINGLE_SELECT" ||
          questionnaireItem?.item.original.type === "TIME" ||
          questionnaireItem?.item.original.type === "TEXT" ||
          questionnaireItem?.item.original.type === "AUTO_TEXT" ||
          questionnaireItem?.item.original.type === "SLIDER") &&
        questionnaireItem.item.original.ecoa?.label
      ) {
        return questionnaireItem.item.original.ecoa.label;
      }
      return questionnaireItem?.question;
    })();

    const questionAlign =
      (questionnaireItem?.item?.original.type === "SINGLE_SELECT" ||
        questionnaireItem?.item?.original.type === "TIME" ||
        questionnaireItem?.item?.original.type === "TEXT") &&
      questionnaireItem?.item?.original.align
        ? questionnaireItem?.item?.original.align
        : "LEFT";

    return (
      <StyledVisitQuestionnaireWrapper isBottomDescription={isBottomDescription}>
        {descriptionText && descriptionPosition === "TOP" && (
          <>
            <HbDivider />
            <EcoaParagraph margin={2} align={"LEFT"} size={descriptionSize} color={descriptionColor} bold={false}>
              {nl2br(descriptionText)}
            </EcoaParagraph>
          </>
        )}
        {questionnaireItem && (
          <VisitQuestionnaireWrapper
            title={questionnaireItem.title}
            question={question}
            questionAlign={questionAlign}
            icon={questionnaireItem.item.original?.labelIcon}
            text={questionnaireItem.text}
          >
            <VisitQuestionnaireInner
              studySpec={studySpec}
              crfs={crfs}
              folderId={folderId}
              visitId={visitId}
              visitSeq={visitSeq}
              crfPageId={crfPageId}
              crfPageSeq={crfPageSeq}
              questionnaire={questionnaireItem}
              questionnaireSeq={questionnaireSeq}
              questionnaireCount={availableQuestionnaires.length}
              questionnairesData={{
                ...questionnairesData,
                questionnaires: availableQuestionnaires as Array<EcoaQuestionnaire | Array<EcoaQuestionnaire>>,
              }}
              captureResponse={captureResponse}
              questionnaireAnswersState={[questionnaireAnswers, setQuestionnaireAnswers]}
              isAvailableItem={isAvailableItem}
            />
          </VisitQuestionnaireWrapper>
        )}
        {isVisibleDescription && descriptionPosition === "BOTTOM" && (
          <EcoaParagraph margin={2} align={"LEFT"} size={descriptionSize} color={descriptionColor} bold={false}>
            {nl2br(descriptionText)}
          </EcoaParagraph>
        )}
      </StyledVisitQuestionnaireWrapper>
    );
  }
}
